import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Search, Upload, FileText, Download, Eye, Trash2, 
  FolderOpen, Loader2, ChevronRight, Home, FolderPlus, Folder, Move, GripVertical
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentsPage = () => {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [documents, setDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]); // For folder count display
  const [allCategories, setAllCategories] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState({ nl: '', de: '', en: '' });
  const [moveOpen, setMoveOpen] = useState(false);
  const [movingFolder, setMovingFolder] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState('root');
  const [moving, setMoving] = useState(false);
  const [draggedDocument, setDraggedDocument] = useState(null);
  const [draggedFolder, setDraggedFolder] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // { folderId, position: 'before' | 'after' | 'into' }
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    category: '',
    description: '',
    file: null
  });
  
  const searchResultsRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Get current folder ID from URL
  const folderId = searchParams.get('folder') || null;

  // Build breadcrumb path
  const getBreadcrumbs = useCallback(() => {
    if (!folderId) return [];
    
    const breadcrumbs = [];
    let current = allCategories.find(c => c.id === folderId);
    
    while (current) {
      breadcrumbs.unshift(current);
      current = current.parent_id ? allCategories.find(c => c.id === current.parent_id) : null;
    }
    
    return breadcrumbs;
  }, [folderId, allCategories]);

  // Get subfolders of current folder
  const getSubfolders = useCallback(() => {
    return allCategories.filter(c => c.parent_id === folderId);
  }, [allCategories, folderId]);

  // Get documents in current folder
  const getCurrentDocuments = useCallback(() => {
    if (searchQuery) {
      // When searching, search all documents
      return documents;
    }
    // Filter by current folder
    return documents.filter(d => d.category === folderId);
  }, [documents, folderId, searchQuery]);

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('search', searchQuery);
      } else if (folderId) {
        params.append('category', folderId);
      }
      
      const res = await axios.get(`${API}/documents?${params}`);
      setDocuments(res.data);
    } catch (e) {
      console.error('Failed to fetch documents:', e);
    } finally {
      setLoading(false);
    }
  }, [folderId, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setAllCategories(res.data);
    } catch (e) {
      console.error('Failed to fetch categories:', e);
    }
  };

  const fetchAllDocuments = async () => {
    try {
      const res = await axios.get(`${API}/documents`);
      setAllDocuments(res.data);
    } catch (e) {
      console.error('Failed to fetch all documents:', e);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAllDocuments();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    // Update current folder object when folderId or categories change
    if (folderId) {
      const folder = allCategories.find(c => c.id === folderId);
      setCurrentFolder(folder || null);
    } else {
      setCurrentFolder(null);
    }
  }, [folderId, allCategories]);

  // Scroll to search results when searching
  useEffect(() => {
    if (searchQuery && searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchQuery, documents]);

  const navigateToFolder = (folder) => {
    if (folder) {
      setSearchParams({ folder: folder.id });
    } else {
      setSearchParams({});
    }
    setSearchQuery(''); // Clear search when navigating
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.name) {
      toast.error(language === 'nl' ? 'Vul alle verplichte velden in' : language === 'de' ? 'Bitte alle Pflichtfelder ausfüllen' : 'Please fill in all required fields');
      return;
    }

    // Use current folder as category, or require selection if at root
    const categoryId = uploadForm.category || folderId;
    if (!categoryId) {
      toast.error(language === 'nl' ? 'Selecteer eerst een map' : language === 'de' ? 'Bitte wählen Sie zuerst einen Ordner' : 'Please select a folder first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('name', uploadForm.name);
    formData.append('category', categoryId);
    formData.append('description', uploadForm.description);
    formData.append('file', uploadForm.file);

    try {
      await axios.post(`${API}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'nl' ? 'Document geüpload' : language === 'de' ? 'Dokument hochgeladen' : 'Document uploaded');
      setUploadOpen(false);
      setUploadForm({ name: '', category: '', description: '', file: null });
      fetchDocuments();
      fetchAllDocuments(); // Update folder counts
    } catch (e) {
      toast.error(language === 'nl' ? 'Upload mislukt' : language === 'de' ? 'Upload fehlgeschlagen' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.nl) {
      toast.error(language === 'nl' ? 'Vul een mapnaam in' : language === 'de' ? 'Bitte geben Sie einen Ordnernamen ein' : 'Please enter a folder name');
      return;
    }

    setCreatingFolder(true);
    try {
      await axios.post(`${API}/categories`, {
        name_nl: newFolderName.nl,
        name_de: newFolderName.de || newFolderName.nl,
        name_en: newFolderName.en || newFolderName.nl,
        icon: 'folder',
        parent_id: folderId
      });
      toast.success(language === 'nl' ? 'Map aangemaakt' : language === 'de' ? 'Ordner erstellt' : 'Folder created');
      setFolderOpen(false);
      setNewFolderName({ nl: '', de: '', en: '' });
      fetchCategories();
    } catch (e) {
      toast.error(language === 'nl' ? 'Aanmaken mislukt' : language === 'de' ? 'Erstellen fehlgeschlagen' : 'Create failed');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleMoveFolder = async () => {
    if (!movingFolder) return;
    
    setMoving(true);
    try {
      const newParentId = selectedDestination === 'root' ? null : selectedDestination;
      await axios.patch(`${API}/categories/${movingFolder.id}/move`, null, {
        params: { new_parent_id: newParentId }
      });
      toast.success(language === 'nl' ? 'Map verplaatst' : language === 'de' ? 'Ordner verschoben' : 'Folder moved');
      setMoveOpen(false);
      setMovingFolder(null);
      setSelectedDestination('root');
      fetchCategories();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || '';
      if (errorMsg.includes('itself')) {
        toast.error(language === 'nl' ? 'Kan map niet naar zichzelf verplaatsen' : language === 'de' ? 'Ordner kann nicht in sich selbst verschoben werden' : 'Cannot move folder into itself');
      } else if (errorMsg.includes('subfolder')) {
        toast.error(language === 'nl' ? 'Kan map niet naar eigen submap verplaatsen' : language === 'de' ? 'Ordner kann nicht in eigenen Unterordner verschoben werden' : 'Cannot move folder into its own subfolder');
      } else {
        toast.error(language === 'nl' ? 'Verplaatsen mislukt' : language === 'de' ? 'Verschieben fehlgeschlagen' : 'Move failed');
      }
    } finally {
      setMoving(false);
    }
  };

  const openMoveDialog = (folder, e) => {
    e.stopPropagation();
    setMovingFolder(folder);
    setSelectedDestination(folder.parent_id || 'root');
    setMoveOpen(true);
  };

  // Get valid destinations for moving (exclude the folder itself and its descendants)
  const getValidDestinations = useCallback(() => {
    if (!movingFolder) return allCategories;
    
    // Get all descendants of the moving folder
    const getDescendants = (folderId) => {
      const children = allCategories.filter(c => c.parent_id === folderId);
      let descendants = [...children];
      children.forEach(child => {
        descendants = [...descendants, ...getDescendants(child.id)];
      });
      return descendants;
    };
    
    const descendants = getDescendants(movingFolder.id);
    const excludeIds = new Set([movingFolder.id, ...descendants.map(d => d.id)]);
    
    return allCategories.filter(c => !excludeIds.has(c.id));
  }, [movingFolder, allCategories]);

  const handleDeleteFolder = async (folder) => {
    const confirmMsg = language === 'nl' 
      ? 'Weet je zeker dat je deze map wilt verwijderen? De map moet leeg zijn.'
      : language === 'de'
      ? 'Sind Sie sicher, dass Sie diesen Ordner löschen möchten? Der Ordner muss leer sein.'
      : 'Are you sure you want to delete this folder? The folder must be empty.';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.delete(`${API}/categories/${folder.id}`);
      toast.success(language === 'nl' ? 'Map verwijderd' : language === 'de' ? 'Ordner gelöscht' : 'Folder deleted');
      fetchCategories();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || '';
      if (errorMsg.includes('subcategories')) {
        toast.error(language === 'nl' ? 'Map bevat submappen' : language === 'de' ? 'Ordner enthält Unterordner' : 'Folder contains subfolders');
      } else if (errorMsg.includes('documents')) {
        toast.error(language === 'nl' ? 'Map bevat documenten' : language === 'de' ? 'Ordner enthält Dokumente' : 'Folder contains documents');
      } else {
        toast.error(language === 'nl' ? 'Verwijderen mislukt' : language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
      }
    }
  };

  // Drag and drop handlers for documents
  const handleDragStart = (e, doc) => {
    if (!isAdmin) return;
    setDraggedDocument(doc);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', doc.id);
    e.dataTransfer.setData('dragType', 'document');
    // Store reference before setTimeout
    const target = e.target;
    setTimeout(() => {
      if (target && target.style) {
        target.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleFolderDragStart = (e, folder) => {
    if (!isAdmin) return;
    e.stopPropagation();
    setDraggedFolder(folder);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', folder.id);
    e.dataTransfer.setData('dragType', 'folder');
    // Store reference before setTimeout
    const target = e.currentTarget;
    setTimeout(() => {
      if (target && target.style) {
        target.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.target && e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedDocument(null);
    setDraggedFolder(null);
    setDragOverFolder(null);
    setDropPosition(null);
    setIsDraggingFiles(false);
  };

  const handleDragOver = (e, folder, index, totalFolders) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging files from desktop
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDraggingFiles(true);
      setDragOverFolder(folder?.id || 'root');
      setDropPosition(null);
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    
    // Document drag - always drop into folder
    if (draggedDocument && folder && folder.id !== draggedDocument.category) {
      setDragOverFolder(folder.id);
      setDropPosition({ folderId: folder.id, position: 'into' });
      return;
    }
    
    // Folder drag - check if sorting or moving into
    if (draggedFolder && folder) {
      const isDescendant = (parentId, childId) => {
        const children = allCategories.filter(c => c.parent_id === parentId);
        if (children.some(c => c.id === childId)) return true;
        return children.some(c => isDescendant(c.id, childId));
      };
      
      // Can't drop into itself or descendants
      if (folder.id === draggedFolder.id || isDescendant(draggedFolder.id, folder.id)) {
        return;
      }
      
      // Determine if sorting (drop between) or moving into (drop on center)
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const height = rect.height;
      
      // Top 25% = before, Bottom 25% = after, Middle 50% = into
      if (y < height * 0.25) {
        setDropPosition({ folderId: folder.id, position: 'before', index });
        setDragOverFolder(null);
      } else if (y > height * 0.75) {
        setDropPosition({ folderId: folder.id, position: 'after', index });
        setDragOverFolder(null);
      } else {
        setDropPosition({ folderId: folder.id, position: 'into' });
        setDragOverFolder(folder.id);
      }
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear if leaving the actual drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverFolder(null);
      setDropPosition(null);
      setIsDraggingFiles(false);
    }
  };

  const handleDrop = async (e, folder, folders) => {
    e.preventDefault();
    e.stopPropagation();
    
    const currentDropPosition = dropPosition;
    setDragOverFolder(null);
    setDropPosition(null);
    setIsDraggingFiles(false);
    
    // Handle file drops from desktop
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFileDrop(e.dataTransfer.files, folder);
      return;
    }
    
    // Handle document drag
    if (draggedDocument && isAdmin) {
      if (draggedDocument.category === folder?.id) {
        setDraggedDocument(null);
        return;
      }
      
      try {
        await axios.patch(`${API}/documents/${draggedDocument.id}/move`, null, {
          params: { category_id: folder.id }
        });
        toast.success(
          language === 'nl' 
            ? `"${draggedDocument.name}" verplaatst naar "${getCategoryName(folder)}"` 
            : language === 'de'
            ? `"${draggedDocument.name}" verschoben nach "${getCategoryName(folder)}"`
            : `"${draggedDocument.name}" moved to "${getCategoryName(folder)}"`
        );
        fetchDocuments();
        fetchAllDocuments(); // Update folder counts
      } catch (err) {
        toast.error(language === 'nl' ? 'Verplaatsen mislukt' : language === 'de' ? 'Verschieben fehlgeschlagen' : 'Move failed');
      } finally {
        setDraggedDocument(null);
      }
      return;
    }
    
    // Handle folder drag
    if (draggedFolder && isAdmin && folder && folders) {
      // Check if we're sorting (before/after) or moving into
      if (currentDropPosition && (currentDropPosition.position === 'before' || currentDropPosition.position === 'after')) {
        // Sorting - reorder folders
        const currentIndex = folders.findIndex(f => f.id === draggedFolder.id);
        let targetIndex = folders.findIndex(f => f.id === folder.id);
        
        if (currentIndex === -1 || targetIndex === -1) {
          setDraggedFolder(null);
          return;
        }
        
        // Adjust target index based on position
        if (currentDropPosition.position === 'after') {
          targetIndex += 1;
        }
        
        // If moving down, account for the removal
        if (currentIndex < targetIndex) {
          targetIndex -= 1;
        }
        
        // Create new order
        const newOrder = [...folders];
        const [removed] = newOrder.splice(currentIndex, 1);
        newOrder.splice(targetIndex, 0, removed);
        
        try {
          await axios.post(`${API}/categories/reorder`, {
            category_ids: newOrder.map(f => f.id)
          });
          toast.success(
            language === 'nl' ? 'Mapvolgorde aangepast' 
            : language === 'de' ? 'Ordnerreihenfolge geändert' 
            : 'Folder order updated'
          );
          fetchCategories();
        } catch (err) {
          toast.error(language === 'nl' ? 'Sorteren mislukt' : language === 'de' ? 'Sortieren fehlgeschlagen' : 'Sort failed');
        } finally {
          setDraggedFolder(null);
        }
        return;
      }
      
      // Moving into folder
      if (draggedFolder.id === folder.id || draggedFolder.parent_id === folder.id) {
        setDraggedFolder(null);
        return;
      }
      
      try {
        await axios.patch(`${API}/categories/${draggedFolder.id}/move`, null, {
          params: { new_parent_id: folder.id }
        });
        toast.success(
          language === 'nl' 
            ? `"${getCategoryName(draggedFolder)}" verplaatst naar "${getCategoryName(folder)}"` 
            : language === 'de'
            ? `"${getCategoryName(draggedFolder)}" verschoben nach "${getCategoryName(folder)}"`
            : `"${getCategoryName(draggedFolder)}" moved to "${getCategoryName(folder)}"`
        );
        fetchCategories();
      } catch (err) {
        const errorMsg = err.response?.data?.detail || '';
        if (errorMsg.includes('itself')) {
          toast.error(language === 'nl' ? 'Kan map niet naar zichzelf verplaatsen' : language === 'de' ? 'Ordner kann nicht in sich selbst verschoben werden' : 'Cannot move folder into itself');
        } else if (errorMsg.includes('subfolder')) {
          toast.error(language === 'nl' ? 'Kan map niet naar eigen submap verplaatsen' : language === 'de' ? 'Ordner kann nicht in eigenen Unterordner verschoben werden' : 'Cannot move folder into its own subfolder');
        } else {
          toast.error(language === 'nl' ? 'Verplaatsen mislukt' : language === 'de' ? 'Verschieben fehlgeschlagen' : 'Move failed');
        }
      } finally {
        setDraggedFolder(null);
      }
    }
  };

  // Handle files dropped from desktop
  const handleFileDrop = async (files, targetFolder) => {
    if (!isAdmin) {
      toast.error(language === 'nl' ? 'Geen toestemming om bestanden te uploaden' : language === 'de' ? 'Keine Berechtigung zum Hochladen' : 'No permission to upload files');
      return;
    }
    
    const categoryId = targetFolder?.id || folderId;
    if (!categoryId) {
      toast.error(language === 'nl' ? 'Selecteer eerst een map' : language === 'de' ? 'Bitte wählen Sie zuerst einen Ordner' : 'Please select a folder first');
      return;
    }
    
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray.map(f => ({ name: f.name, progress: 0 })));
    
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      // Use filename without extension as document name
      const docName = file.name.replace(/\.[^/.]+$/, '');
      
      const formData = new FormData();
      formData.append('name', docName);
      formData.append('category', categoryId);
      formData.append('description', '');
      formData.append('file', file);
      
      try {
        await axios.post(`${API}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadingFiles(prev => prev.map((f, idx) => 
              idx === i ? { ...f, progress } : f
            ));
          }
        });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    
    setUploadingFiles([]);
    
    if (successCount > 0) {
      toast.success(
        language === 'nl' 
          ? `${successCount} bestand${successCount > 1 ? 'en' : ''} geüpload` 
          : language === 'de'
          ? `${successCount} Datei${successCount > 1 ? 'en' : ''} hochgeladen`
          : `${successCount} file${successCount > 1 ? 's' : ''} uploaded`
      );
      fetchDocuments();
      fetchAllDocuments(); // Update folder counts
    }
    if (failCount > 0) {
      toast.error(
        language === 'nl' 
          ? `${failCount} bestand${failCount > 1 ? 'en' : ''} mislukt` 
          : language === 'de'
          ? `${failCount} Datei${failCount > 1 ? 'en' : ''} fehlgeschlagen`
          : `${failCount} file${failCount > 1 ? 's' : ''} failed`
      );
    }
  };

  // Global drop zone for the page
  const handlePageDragOver = (e) => {
    if (e.dataTransfer.types.includes('Files') && isAdmin && folderId) {
      e.preventDefault();
      setIsDraggingFiles(true);
    }
  };

  const handlePageDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingFiles(false);
    }
  };

  const handlePageDrop = async (e) => {
    e.preventDefault();
    setIsDraggingFiles(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && isAdmin && folderId) {
      await handleFileDrop(e.dataTransfer.files, currentFolder);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(language === 'nl' ? 'Download mislukt' : language === 'de' ? 'Download fehlgeschlagen' : 'Download failed');
    }
  };

  const handleDeleteDocument = async (docId) => {
    const confirmMsg = language === 'nl' 
      ? 'Weet je zeker dat je dit document wilt verwijderen?'
      : language === 'de'
      ? 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?'
      : 'Are you sure you want to delete this document?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success(language === 'nl' ? 'Document verwijderd' : language === 'de' ? 'Dokument gelöscht' : 'Document deleted');
      fetchDocuments();
      fetchAllDocuments(); // Update folder counts
    } catch (e) {
      toast.error(language === 'nl' ? 'Verwijderen mislukt' : language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
    }
  };

  const getCategoryName = (cat) => {
    if (!cat) return '';
    return language === 'nl' ? cat.name_nl : language === 'de' ? cat.name_de : (cat.name_en || cat.name_nl);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileTypeColor = (type) => {
    const colors = {
      pdf: 'text-red-500 bg-red-100 dark:bg-red-500/10',
      doc: 'text-blue-500 bg-blue-100 dark:bg-blue-500/10',
      docx: 'text-blue-500 bg-blue-100 dark:bg-blue-500/10',
      xls: 'text-green-500 bg-green-100 dark:bg-green-500/10',
      xlsx: 'text-green-500 bg-green-100 dark:bg-green-500/10',
      png: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
      jpg: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
      jpeg: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
    };
    return colors[type] || 'text-zinc-500 bg-zinc-100 dark:bg-zinc-500/10';
  };

  const breadcrumbs = getBreadcrumbs();
  const subfolders = getSubfolders();
  const currentDocuments = getCurrentDocuments();
  const rootFolders = allCategories.filter(c => !c.parent_id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-foreground">{t('nav.documents')}</h1>
        
        {isAdmin && (
          <div className="flex gap-2">
            <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-border text-foreground" data-testid="create-folder-btn">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  {language === 'nl' ? 'Nieuwe map' : language === 'de' ? 'Neuer Ordner' : 'New folder'}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="text-foreground">
                    {language === 'nl' ? 'Nieuwe map aanmaken' : language === 'de' ? 'Neuen Ordner erstellen' : 'Create new folder'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateFolder} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">
                      {language === 'nl' ? 'Mapnaam (Nederlands)' : language === 'de' ? 'Ordnername (Niederländisch)' : 'Folder name (Dutch)'} *
                    </Label>
                    <Input
                      value={newFolderName.nl}
                      onChange={(e) => setNewFolderName({ ...newFolderName, nl: e.target.value })}
                      className="bg-background border-border text-foreground"
                      required
                      data-testid="folder-name-nl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">
                      {language === 'nl' ? 'Mapnaam (Duits)' : language === 'de' ? 'Ordnername (Deutsch)' : 'Folder name (German)'}
                    </Label>
                    <Input
                      value={newFolderName.de}
                      onChange={(e) => setNewFolderName({ ...newFolderName, de: e.target.value })}
                      className="bg-background border-border text-foreground"
                      data-testid="folder-name-de"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">
                      {language === 'nl' ? 'Mapnaam (Engels)' : language === 'de' ? 'Ordnername (Englisch)' : 'Folder name (English)'}
                    </Label>
                    <Input
                      value={newFolderName.en}
                      onChange={(e) => setNewFolderName({ ...newFolderName, en: e.target.value })}
                      className="bg-background border-border text-foreground"
                      data-testid="folder-name-en"
                    />
                  </div>
                  {currentFolder && (
                    <p className="text-sm text-muted-foreground">
                      {language === 'nl' ? 'Map wordt aangemaakt in: ' : language === 'de' ? 'Ordner wird erstellt in: ' : 'Folder will be created in: '}
                      <span className="font-medium text-foreground">{getCategoryName(currentFolder)}</span>
                    </p>
                  )}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setFolderOpen(false)} className="border-border text-foreground">
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={creatingFolder} data-testid="create-folder-submit">
                      {creatingFolder && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {language === 'nl' ? 'Aanmaken' : language === 'de' ? 'Erstellen' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 btn-glow" data-testid="upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {t('docs.upload')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="text-foreground">{t('docs.upload')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">{t('docs.name')} *</Label>
                    <Input
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                      className="bg-background border-border text-foreground"
                      required
                      data-testid="upload-name-input"
                    />
                  </div>
                  {!folderId && (
                    <div className="space-y-2">
                      <Label className="text-foreground">{t('docs.category')} *</Label>
                      <Select 
                        value={uploadForm.category} 
                        onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
                      >
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="upload-category-select">
                          <SelectValue placeholder={language === 'nl' ? 'Selecteer map' : language === 'de' ? 'Ordner wählen' : 'Select folder'} />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {allCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">
                              {getCategoryName(cat)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {folderId && (
                    <p className="text-sm text-muted-foreground">
                      {language === 'nl' ? 'Uploaden naar: ' : language === 'de' ? 'Hochladen nach: ' : 'Upload to: '}
                      <span className="font-medium text-foreground">{getCategoryName(currentFolder)}</span>
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label className="text-foreground">{t('docs.description')}</Label>
                    <Textarea
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                      className="bg-background border-border text-foreground"
                      rows={3}
                      data-testid="upload-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">{t('docs.file')} *</Label>
                    <Input
                      type="file"
                      onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                      className="bg-background border-border text-foreground file:bg-muted file:text-foreground file:border-0 file:mr-4"
                      required
                      data-testid="upload-file-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} className="border-border text-foreground">
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={uploading} data-testid="upload-submit-btn">
                      {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {t('docs.upload')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('docs.search')}
          className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
          data-testid="search-input"
        />
      </div>

      {/* Breadcrumbs */}
      {!searchQuery && (
        <nav className="flex items-center gap-1 text-sm flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateToFolder(null)}
            className={`text-muted-foreground hover:text-foreground ${!folderId ? 'text-foreground font-medium' : ''}`}
          >
            <Home className="w-4 h-4 mr-1" />
            {language === 'nl' ? 'Alle mappen' : language === 'de' ? 'Alle Ordner' : 'All folders'}
          </Button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateToFolder(crumb)}
                className={`text-muted-foreground hover:text-foreground ${index === breadcrumbs.length - 1 ? 'text-foreground font-medium' : ''}`}
              >
                {getCategoryName(crumb)}
              </Button>
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Search results indicator */}
      {searchQuery && (
        <div ref={searchResultsRef} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            {language === 'nl' 
              ? `${currentDocuments.length} resultaten voor "${searchQuery}"` 
              : language === 'de'
              ? `${currentDocuments.length} Ergebnisse für "${searchQuery}"`
              : `${currentDocuments.length} results for "${searchQuery}"`}
            <Button 
              variant="link" 
              size="sm" 
              onClick={() => setSearchQuery('')}
              className="text-blue-600 dark:text-blue-400 ml-2 p-0 h-auto"
            >
              {language === 'nl' ? 'Wissen' : language === 'de' ? 'Löschen' : 'Clear'}
            </Button>
          </p>
        </div>
      )}

      {/* Folders Grid */}
      {!searchQuery && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {(folderId ? subfolders : rootFolders).map((folder, index, arr) => (
            <div key={folder.id} className="relative">
              {/* Drop indicator before */}
              {dropPosition?.folderId === folder.id && dropPosition?.position === 'before' && (
                <div className="absolute -top-2 left-0 right-0 h-1 bg-primary rounded-full z-10" />
              )}
              <Card 
                className={`bg-card border-border hover:border-primary/50 cursor-pointer transition-all group ${
                  dragOverFolder === folder.id ? 'border-primary border-2 bg-primary/5 scale-105' : ''
                } ${(draggedDocument || draggedFolder || isDraggingFiles) ? 'ring-2 ring-primary/20' : ''} ${
                  draggedFolder?.id === folder.id ? 'opacity-50' : ''
                } ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                draggable={isAdmin}
                onDragStart={(e) => handleFolderDragStart(e, folder)}
                onDragEnd={handleDragEnd}
                onClick={() => !draggedDocument && !draggedFolder && navigateToFolder(folder)}
                onDragOver={(e) => handleDragOver(e, folder, index, arr.length)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folder, arr)}
                data-testid={`folder-${folder.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {isAdmin && (
                        <div className="text-muted-foreground cursor-grab">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}
                      <div className={`p-2 rounded-lg transition-colors ${
                        dragOverFolder === folder.id ? 'bg-primary/30' : 'bg-primary/10 group-hover:bg-primary/20'
                      }`}>
                        <Folder className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-foreground font-medium group-hover:text-primary transition-colors">
                          {getCategoryName(folder)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {allDocuments.filter(d => d.category === folder.id).length} {language === 'nl' ? 'bestanden' : language === 'de' ? 'Dateien' : 'files'}
                          {dragOverFolder === folder.id && (
                            <span className="ml-1 text-primary font-medium">
                              {isDraggingFiles 
                                ? (language === 'nl' ? '← Upload hier' : language === 'de' ? '← Hier hochladen' : '← Upload here')
                                : (language === 'nl' ? '← Laat hier los' : language === 'de' ? '← Hier ablegen' : '← Drop here')
                              }
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  {isAdmin && !draggedDocument && !draggedFolder && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary hover:bg-primary/10 h-8 w-8"
                        onClick={(e) => openMoveDialog(folder, e)}
                        title={language === 'nl' ? 'Verplaatsen' : language === 'de' ? 'Verschieben' : 'Move'}
                        data-testid={`move-folder-${folder.id}`}
                      >
                        <Move className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(folder);
                        }}
                        data-testid={`delete-folder-${folder.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              {/* Drop indicator after */}
              {dropPosition?.folderId === folder.id && dropPosition?.position === 'after' && (
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-primary rounded-full z-10" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state for folders */}
      {!searchQuery && !loading && (folderId ? subfolders : rootFolders).length === 0 && currentDocuments.length === 0 && (
        <Card 
          className={`bg-card border-border transition-all ${isDraggingFiles ? 'border-primary border-2 border-dashed bg-primary/5' : ''}`}
          onDragOver={handlePageDragOver}
          onDragLeave={handlePageDragLeave}
          onDrop={handlePageDrop}
        >
          <CardContent className="p-8 text-center">
            {isDraggingFiles ? (
              <>
                <Upload className="w-12 h-12 text-primary mx-auto mb-4 animate-bounce" />
                <p className="text-primary font-medium">
                  {language === 'nl' ? 'Laat bestanden hier los om te uploaden' : language === 'de' ? 'Dateien hier ablegen zum Hochladen' : 'Drop files here to upload'}
                </p>
              </>
            ) : (
              <>
                <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'nl' ? 'Deze map is leeg' : language === 'de' ? 'Dieser Ordner ist leer' : 'This folder is empty'}
                </p>
                {isAdmin && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {language === 'nl' ? 'Maak een submap aan, upload een document, of sleep bestanden hierheen' : language === 'de' ? 'Erstellen Sie einen Unterordner, laden Sie ein Dokument hoch, oder ziehen Sie Dateien hierher' : 'Create a subfolder, upload a document, or drag files here'}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload progress indicator */}
      {uploadingFiles.length > 0 && (
        <Card className="bg-card border-border border-primary">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-foreground mb-3">
              {language === 'nl' ? 'Bestanden uploaden...' : language === 'de' ? 'Dateien werden hochgeladen...' : 'Uploading files...'}
            </p>
            <div className="space-y-2">
              {uploadingFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground truncate">{file.name}</p>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                      <div 
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{file.progress}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents list */}
      {(currentDocuments.length > 0 || (folderId && !searchQuery)) && (
        <Card 
          className={`bg-card border-border transition-all ${isDraggingFiles && folderId ? 'border-primary border-2 border-dashed' : ''}`}
          onDragOver={handlePageDragOver}
          onDragLeave={handlePageDragLeave}
          onDrop={handlePageDrop}
        >
          <CardHeader className="border-b border-border">
            <CardTitle className="text-foreground">
              {searchQuery 
                ? (language === 'nl' ? 'Zoekresultaten' : language === 'de' ? 'Suchergebnisse' : 'Search results')
                : (language === 'nl' ? 'Bestanden' : language === 'de' ? 'Dateien' : 'Files')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isDraggingFiles && folderId && (
              <div className="p-6 text-center border-b border-border bg-primary/5">
                <Upload className="w-8 h-8 text-primary mx-auto mb-2 animate-bounce" />
                <p className="text-primary font-medium">
                  {language === 'nl' ? 'Laat bestanden hier los om te uploaden' : language === 'de' ? 'Dateien hier ablegen zum Hochladen' : 'Drop files here to upload'}
                </p>
              </div>
            )}
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              </div>
            ) : currentDocuments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {language === 'nl' ? 'Geen documenten gevonden' : language === 'de' ? 'Keine Dokumente gefunden' : 'No documents found'}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {currentDocuments.map((doc) => (
                  <div 
                    key={doc.id} 
                    className={`flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors ${
                      isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
                    } ${draggedDocument?.id === doc.id ? 'opacity-50 bg-accent' : ''}`}
                    draggable={isAdmin}
                    onDragStart={(e) => handleDragStart(e, doc)}
                    onDragEnd={handleDragEnd}
                    data-testid={`doc-row-${doc.id}`}
                  >
                    {isAdmin && (
                      <div className="text-muted-foreground hover:text-foreground cursor-grab">
                        <GripVertical className="w-4 h-4" />
                      </div>
                    )}
                    <div className={`p-2 rounded-lg ${getFileTypeColor(doc.file_type)}`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/documents/${doc.id}`} className="text-foreground font-medium hover:text-primary truncate block">
                        {doc.name}
                      </Link>
                      <p className="text-muted-foreground text-sm">
                        {formatFileSize(doc.file_size)} • {doc.file_type?.toUpperCase()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link to={`/documents/${doc.id}`}>
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8" data-testid={`view-btn-${doc.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-secondary hover:text-secondary hover:bg-secondary/10 h-8 w-8"
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-btn-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                          onClick={() => handleDeleteDocument(doc.id)}
                          data-testid={`delete-btn-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Move Folder Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {language === 'nl' ? 'Map verplaatsen' : language === 'de' ? 'Ordner verschieben' : 'Move folder'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {movingFolder && (
              <p className="text-sm text-muted-foreground">
                {language === 'nl' ? 'Verplaats' : language === 'de' ? 'Verschiebe' : 'Move'}{' '}
                <span className="font-medium text-foreground">"{getCategoryName(movingFolder)}"</span>{' '}
                {language === 'nl' ? 'naar:' : language === 'de' ? 'nach:' : 'to:'}
              </p>
            )}
            <Select value={selectedDestination} onValueChange={setSelectedDestination}>
              <SelectTrigger className="bg-background border-border text-foreground" data-testid="move-destination-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-60">
                <SelectItem value="root" className="text-foreground hover:bg-accent">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    {language === 'nl' ? 'Hoofdmap (root)' : language === 'de' ? 'Stammordner (root)' : 'Root folder'}
                  </div>
                </SelectItem>
                {getValidDestinations().map((cat) => (
                  <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">
                    <div className="flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      {getCategoryName(cat)}
                      {cat.parent_id && (
                        <span className="text-muted-foreground text-xs">
                          ({language === 'nl' ? 'in' : language === 'de' ? 'in' : 'in'} {getCategoryName(allCategories.find(c => c.id === cat.parent_id))})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setMoveOpen(false)} 
                className="border-border text-foreground"
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleMoveFolder} 
                className="bg-primary hover:bg-primary/90" 
                disabled={moving}
                data-testid="move-folder-submit"
              >
                {moving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {language === 'nl' ? 'Verplaatsen' : language === 'de' ? 'Verschieben' : 'Move'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
